import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faClock } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

const RestaurantCard = ({
  id,
  name,
  image,
  avgRating,
  avgRatingUser,
  estimated_delivery_time,
  is_open,
  restroType,
  description,
  address,
  coverImg,
  locationSearchVisibility,
}) => {
  // Determine which rating to display (prefer avgRating if > 0, else avgRatingUser)
  const displayRating = avgRating > 0 ? avgRating : avgRatingUser;
  const hasRating = displayRating > 0;
  
  // Format address for display (show last 3 parts)
  const formattedAddress = address?.split(",").slice(-3).join(",").trim();
  
  // Truncate description
  const truncatedDescription = description?.length > 80 
    ? description.substring(0, 80) + "..." 
    : description;

  return (
    <div key={id} className={`${locationSearchVisibility ? "-z-10" : ""}`}>
      <Link
        to={is_open ? `/restaurant/${id}` : "#"}
        className={`grid grid-flow-row justify-stretch h-auto transition-all duration-300 ease-in-out hover:scale-95 hover:origin-center ${
          is_open ? "cursor-pointer" : "cursor-not-allowed opacity-60"
        } sm:w-72 lg:w-64`}
      >
        {/* Image Section */}
        <div className="relative w-full h-48 sm:w-72 lg:h-40 lg:w-64">
          <img
            className="w-full h-full object-cover rounded-2xl"
            src={image || coverImg || "https://via.placeholder.com/300x200?text=No+Image"}
            alt={name}
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
            }}
          />

          {/* Veg/Non-Veg Tag */}
          {restroType && (
            <span className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full font-semibold">
              {restroType === "veg" ? "🟢 Pure Veg" : "🔴 Non-Veg"}
            </span>
          )}

          {/* Open/Closed Status */}
          {is_open ? (
            <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
              Open Now
            </span>
          ) : (
            <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
              Closed
            </span>
          )}
        </div>

        {/* Content Section */}
        <div className="pt-2">
          {/* Restaurant Name */}
          <h1 className="w-64 text-xl font-bold text-gray-800" title={name}>
            {name}
          </h1>

          {/* Rating and Delivery Time */}
          <div className="flex items-center gap-2 mt-1">
            {hasRating ? (
              <div className="flex items-center gap-1 bg-green-100 px-2 py-0.5 rounded">
                <FontAwesomeIcon
                  icon={faStar}
                  className="text-green-700 text-xs"
                />
                <span className="font-semibold text-green-700 text-sm">
                  {displayRating}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">
                <span className="font-semibold text-gray-600 text-sm">New</span>
              </div>
            )}
            <span className="text-gray-400">•</span>
            <div className="flex items-center gap-1">
              <FontAwesomeIcon icon={faClock} className="text-gray-500 text-xs" />
              <span className="text-sm text-gray-600">{estimated_delivery_time || "30-40 mins"}</span>
            </div>
          </div>

          {/* Description / Cuisines */}
          <div className="truncate w-64 mt-1">
            <p className="text-gray-600 text-sm font-medium">
              {restroType === "veg" ? "Pure Vegetarian" : "Non-Vegetarian"} • {truncatedDescription || "Delicious food served here"}
            </p>
          </div>

          {/* Address */}
          {formattedAddress && (
            <div className="truncate w-64 mt-1">
              <p className="text-gray-400 text-xs">{formattedAddress}</p>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default RestaurantCard;